import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useEffect, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { fetchMonthlyRevenue } from "../api/revenue";
import { formatRevenue } from "../utils/revenueFormat";
import { useIsMobile } from "../utils/useIsMobile";
import "../css/RevenueAnalysis.css";
import AdCategoryTable from "../modules/revenue/components/AdCategoryTable";
import { PAYMENT_META } from "../modules/revenue/paymentMeta";
import SubscriptionDetailTable from "../modules/revenue/components/SubscriptionDetailTable";
import SubscriptionPriceTypeSummary from "../modules/revenue/components/SubscriptionPriceTypeSummary";
import WeeklyRevenueChart from "../modules/revenue/components/WeeklyRevenueChart";
import "../css/AdCategoryTable.css";
import "../css/SubscriptionDetailTable.css";
import Layout from "./Layout";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
);

const legendMarginPlugin = {
  id: "legendMargin",
  beforeInit(chart) {
    const originalFit = chart.legend.fit;
    chart.legend.fit = function () {
      originalFit.call(this);
      this.height += 12;
    };
  },
};

// "2026-05" → "2026년 5월"
function formatMonthLabel(yearMonth) {
  const [year, month] = yearMonth.split("-");
  return `${year}년 ${parseInt(month)}월`;
}

// "2026-05" → "5월"
function toMonthLabel(yearMonth) {
  return `${parseInt(yearMonth.split("-")[1])}월`;
}

// 현재 날짜 기준 금월 (YYYY-MM)
function getInitialMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

// 금월 기준 과거 12개월 고정 목록 (내림차순)
function getFixedMonthOptions() {
  const now = new Date();
  const options = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    options.push(`${year}-${month}`);
  }
  return options;
}

// "2026-06-02T14:39:33+0900" → "2026-06-02 07:00"
function formatGeneratedAt(generatedAt) {
  if (!generatedAt) return null;
  const datePart = generatedAt.split("T")[0];
  return `${datePart} 07:00`;
}

// AI 요약 텍스트에서 금액·건수·명 패턴을 하이라이트 span으로 변환
// 복합 숫자(1만4천 원, 27만5천 원 등) 전체를 하나의 토큰으로 매칭
function highlightSummaryText(text) {
  if (!text) return null;
  const parts = text.split(
    /([\d,.]+(?:억[\d,.]*)?(?:만[\d,.]*)?(?:천[\d,.]*)?\s*원|[\d,.]+\s*(?:건|명))/g,
  );
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="ai-highlight">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

function MomBadge({ mom, showLabel = true }) {
  if (mom == null) return <span className="kpi-mom mom-neutral">-</span>;
  if (Math.abs(mom) < 0.0005)
    return <span className="kpi-mom mom-neutral">— 0.0%</span>;
  const pct = (mom * 100).toFixed(1);
  if (mom > 0) {
    return (
      <div>
        {showLabel && <span className="mom-label">전월 대비 </span>}
        <span className="kpi-mom mom-up">▲ {pct}%</span>
      </div>
    );
  }
  return (
    <div>
      {showLabel && <span className="mom-label">전월 대비 </span>}
      <span className="kpi-mom mom-down">▼ {Math.abs(pct)}%</span>
    </div>
  );
}

// 구독은 결제수단 필터 영향 없음 — 필터된 추이·주간 행의 구독 값을 전체(all) 행 값으로 맞춘다
function withAllSubscription(rows, allRows, keyOf) {
  const subscriptionByKey = new Map(
    allRows.map((row) => [keyOf(row), row.subscriptionRevenue]),
  );
  return rows.map((row) => ({
    ...row,
    subscriptionRevenue:
      subscriptionByKey.get(keyOf(row)) ?? row.subscriptionRevenue,
  }));
}

function GradeRevenueBar({ subscriptionDetail }) {
  const grades = [
    { key: "basic", label: "Basic", color: "#85B7EB" },
    { key: "standard", label: "Standard", color: "#378ADD" },
    { key: "premium", label: "Premium", color: "#185FA5" },
  ];

  const revenues = grades.map(
    (g) => subscriptionDetail?.[g.key]?.netRevenue ?? 0,
  );
  const maxRevenue = Math.max(...revenues, 1); // 0 나누기 방지
  const total = revenues.reduce((a, b) => a + b, 0);

  return (
    <div className="grade-bar-list">
      {grades.map((g, i) => {
        const revenue = revenues[i];
        const widthPct = (revenue / maxRevenue) * 100;
        const ratioPct =
          total > 0 ? ((revenue / total) * 100).toFixed(1) : "0.0";
        return (
          <div key={g.key} className="grade-bar-row">
            <div className="grade-bar-top">
              <div className="grade-bar-label">
                <span
                  className="grade-bar-dot"
                  style={{ backgroundColor: g.color }}
                />
                {g.label}
              </div>
              <div className="grade-bar-info">
                {formatRevenue(revenue)}{" "}
                <span className="grade-bar-ratio">({ratioPct}%)</span>
              </div>
            </div>
            <div className="grade-bar-track">
              <div
                className="grade-bar-fill"
                style={{ width: `${widthPct}%`, backgroundColor: g.color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RevenueAnalysis() {
  const isMobile = useIsMobile();
  const [monthOptions] = useState(getFixedMonthOptions);
  const [selectedMonth, setSelectedMonth] = useState(getInitialMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  // selectedMonth 변경 시 API 호출
  useEffect(() => {
    if (!selectedMonth) return;
    // 월을 빠르게 바꿀 때 늦게 도착한 이전 달 응답이 화면을 덮어쓰지 않게
    let ignore = false;
    setLoading(true);
    // 조회 실패 시 이전 달 숫자가 새 달 제목 아래 남지 않게 먼저 비운다
    setData(null);
    setSelectedPaymentMethod(null);
    fetchMonthlyRevenue(selectedMonth)
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => console.error("매출 데이터 로드 실패:", err))
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [selectedMonth]);

  const activeFilter = selectedPaymentMethod || "all";
  const activeKpi = data?.filtered?.[activeFilter]?.kpi || {};
  // 추이·주간 차트: 캠페인은 필터 기준, 구독은 항상 전체(all) 기준
  const activeTrend = withAllSubscription(
    data?.filtered?.[activeFilter]?.trend12m || [],
    data?.filtered?.all?.trend12m || [],
    (t) => t.yearMonth,
  );

  // activeFilter 따라 변경되는 데이터
  const activeCategories = data?.filtered?.[activeFilter]?.categories || [];
  const activeWeekly = withAllSubscription(
    data?.filtered?.[activeFilter]?.weekly || [],
    data?.filtered?.all?.weekly || [],
    (w) => w.weekLabel,
  );

  // 구독 상세 — 결제수단 필터 영향 없음, 항상 전체 데이터
  const subscriptionDetail = data?.subscriptionDetail ?? null;

  // 구독 매출은 항상 filtered.all 고정
  const subscriptionRevenue = data?.filtered?.all?.kpi?.subscriptionRevenue;
  const subscriptionRevenueMom =
    data?.filtered?.all?.kpi?.subscriptionRevenueMom;

  // 결제 건수는 항상 전체(all) 기준 — 필터 비적용
  const paymentCount = data?.filtered?.all?.kpi?.paymentCount;
  const paymentCountMom = data?.filtered?.all?.kpi?.paymentCountMom;

  const handlePaymentMethodClick = (method) => {
    setSelectedPaymentMethod((prev) => (prev === method ? null : method));
  };

  // 최근 12개월 추이 차트 — 스택 막대 (캠페인 + 구독)
  const trendTickFormat = (v) => (v === 0 ? "₩0" : formatRevenue(v));
  const subTickFormat = (v) => {
    if (v === 0) return "₩0";
    if (v >= 1_000_000) return `₩${Math.round(v / 10000)}만`;
    return `₩${v.toLocaleString("ko-KR")}`;
  };

  const trendChartData = {
    labels: activeTrend.map((t) => toMonthLabel(t.yearMonth)),
    datasets: [
      {
        label: "캠페인",
        data: activeTrend.map((t) => t.campaignRevenue ?? 0),
        backgroundColor: "#378ADD",
        yAxisID: "y",
      },
      {
        type: "line",
        label: "구독",
        data: activeTrend.map((t) => t.subscriptionRevenue ?? 0),
        borderColor: "#1D9E75",
        backgroundColor: "#1D9E75",
        pointRadius: 3,
        pointHoverRadius: 5,
        pointHitRadius: 16,
        borderWidth: 2,
        order: -1,
        yAxisID: "y1",
      },
    ],
  };

  // 모바일: 축 글자를 줄이고 x축 월 라벨을 눕히지 않는다 (좁은 폭에서 막대 영역 확보)
  const mobileTickFont = isMobile ? { font: { size: 10 } } : {};

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: isMobile ? { ticks: { maxRotation: 0, ...mobileTickFont } } : {},
      y: {
        min: 0,
        ticks: { callback: trendTickFormat, ...mobileTickFont },
      },
      y1: {
        position: "right",
        min: 0,
        ticks: { callback: subTickFormat, ...mobileTickFont },
        grid: { drawOnChartArea: false },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "start",
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const raw = ctx.raw;
            const label = ctx.dataset.label;
            if (ctx.dataset.yAxisID === "y1") {
              if (raw === 0) return `${label}: ₩0`;
              if (raw >= 1_000_000)
                return `${label}: ₩${Math.round(raw / 10000)}만`;
              return `${label}: ₩${raw.toLocaleString("ko-KR")}`;
            }
            return `${label}: ${formatRevenue(raw)}`;
          },
        },
      },
    },
  };

  // 결제수단 비중 도넛 — 필터 영향 없음, 항상 전체 데이터
  const donutRaw = data?.paymentMethodDonut || {};

  // PAYMENT_META 순서에 따라 라벨·색상·데이터 일관성 유지
  const donutEntries = Object.entries(PAYMENT_META).map(([key, meta]) => ({
    key,
    label: meta.label,
    color: meta.color,
    value: donutRaw[key] ?? 0,
  }));

  const donutChartData = {
    labels: donutEntries.map(e => e.label),
    datasets: [{
      data: donutEntries.map(e => e.value),
      backgroundColor: donutEntries.map(e => e.color),
    }],
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "right" },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct =
              total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : "0.0";
            return `${ctx.label}: ${formatRevenue(ctx.raw)} (${pct}%)`;
          },
        },
      },
    },
  };

  const paymentCards = data?.paymentMethodCards || {};

  return (
    <Layout>
      <div className="revenue-container">
        {/* 헤더 */}
        <div>
          <p className="revenue-sub-header">매출분석 / 월별 매출 현황</p>
          <div className="revenue-header">
            <h4>
              {selectedMonth
                ? `${formatMonthLabel(selectedMonth)} 매출 현황`
                : "매출 현황"}
            </h4>
            <select
              className="form-select"
              value={selectedMonth || ""}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {formatMonthLabel(m)}
                </option>
              ))}
            </select>
          </div>
          {data?.generatedAt && (
            <p className="revenue-last-updated">
              마지막 업데이트: {formatGeneratedAt(data.generatedAt)}
            </p>
          )}
        </div>

        {loading && (
          <div className="revenue-loading">데이터를 불러오는 중...</div>
        )}

        {!loading && !data && selectedMonth && (
          <div className="revenue-empty">
            선택한 월에 매출 데이터가 없습니다. 다른 월을 선택하세요.
          </div>
        )}

        {!loading && data && (
          <>
            {/* AI 요약 */}
            {data.aiSummary && (
              <div className="ai-summary-card">
                <div className="ai-summary-icon">✨</div>
                <div>
                  <div className="ai-summary-label">AI 요약</div>
                  <div className="ai-summary-text">{highlightSummaryText(data.aiSummary.text)}</div>
                </div>
              </div>
            )}

            {selectedPaymentMethod && (
              <div className="payment-filter-badge">
                {PAYMENT_META[selectedPaymentMethod]?.label}만 보기
                <button
                  className="payment-filter-clear"
                  onClick={() => setSelectedPaymentMethod(null)}
                >
                  ✕
                </button>
              </div>
            )}

            {/* KPI 카드 */}
            <div className="kpi-cards">
              <div className="kpi-card">
                <div className="kpi-title">매출 총액</div>
                <div className="kpi-value">
                  {formatRevenue(activeKpi.totalRevenue)}
                </div>
                <MomBadge mom={activeKpi.totalRevenueMom} />
              </div>
              <div className="kpi-card">
                <div className="kpi-title">캠페인 매출</div>
                <div className="kpi-value">
                  {formatRevenue(activeKpi.campaignRevenue)}
                </div>
                <MomBadge mom={activeKpi.campaignRevenueMom} />
              </div>
              <div className="kpi-card">
                <div className="kpi-title">구독 매출</div>
                <div className="kpi-value">
                  {formatRevenue(subscriptionRevenue)}
                </div>
                <MomBadge mom={subscriptionRevenueMom} />
              </div>
              <div className="kpi-card">
                <div className="kpi-title">결제 건수</div>
                <div className="kpi-value">
                  {paymentCount != null
                    ? paymentCount.toLocaleString("ko-KR") + "건"
                    : "-"}
                </div>
                <MomBadge mom={paymentCountMom} />
              </div>
            </div>

            {/* 최근 12개월 추이 */}
            <div className="chart-section">
              <h5>최근 12개월 매출 추이</h5>
              <div style={{ height: 260 }}>
                <Bar
                  key={`${selectedMonth}-${activeFilter}`}
                  data={trendChartData}
                  options={trendChartOptions}
                  plugins={[legendMarginPlugin]}
                />
              </div>
            </div>

            {/* 결제수단별 매출 카드 */}
            <div className="chart-section">
              <div className="payment-section-header">
                <span className="payment-section-title">결제수단별 매출</span>
                <p className="subText">
                  카드를 클릭하면 해당 결제수단의 매출만 필터링됩니다 (결제완료
                  상태 기준)
                </p>
                {/* {selectedPaymentMethod && (
                  <span className="payment-filter-badge">
                    {PAYMENT_META[selectedPaymentMethod]?.label}만 보기
                    <button
                      className="payment-filter-clear"
                      onClick={() => setSelectedPaymentMethod(null)}
                    >
                      ✕
                    </button>
                  </span>
                )} */}
              </div>
              <div className="payment-cards">
                {Object.entries(PAYMENT_META).map(([key, meta]) => {
                  const card = paymentCards[key] || {};
                  const isSelected = selectedPaymentMethod === key;
                  return (
                    <div
                      key={key}
                      className={`payment-card${isSelected ? " selected" : ""}`}
                      style={{ "--payment-color": meta.color }}
                      onClick={() => handlePaymentMethodClick(key)}
                    >
                      <div className="payment-label">
                        <span
                          className="payment-dot"
                          style={{ backgroundColor: meta.color }}
                        />
                        {meta.label}
                      </div>
                      <div className="payment-amount">
                        {formatRevenue(card.amount)}
                      </div>
                      <div className="payment-bottom-row">
                        <span className="payment-ratio">
                          캠페인 매출의{" "}
                          {card.ratio != null
                            ? (card.ratio * 100).toFixed(1) + "%"
                            : "-"}
                        </span>
                        <MomBadge mom={card.mom} showLabel={false} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 도넛 + 구독 등급 */}
            <div className="charts-row">
              <div className="chart-section">
                <h5>결제수단 비중</h5>
                <div className="charts-row-inner">
                  <Doughnut data={donutChartData} options={donutOptions} />
                </div>
              </div>
              <div className="chart-section">
                <h5>구독 등급별 수익</h5>
                <div className="charts-row-inner">
                  <GradeRevenueBar
                    subscriptionDetail={data?.subscriptionDetail}
                  />
                </div>
              </div>
            </div>

            {/* 광고 카테고리별 매출 */}
            <div className="chart-section">
              <div className="section-title-row">
                <h5>광고 카테고리별 매출</h5>
                {selectedPaymentMethod && (
                  <span className="section-filter-badge">
                    <span className="section-filter-label">
                      결제수단 필터:{" "}
                    </span>
                    <span className="section-filter-value">
                      {PAYMENT_META[selectedPaymentMethod]?.label}
                    </span>
                  </span>
                )}
              </div>
              <AdCategoryTable categories={activeCategories} />
            </div>

            {/* 구독 매출 상세 */}
            {subscriptionDetail && (
              <div className="chart-section">
                <h5>구독 매출 상세</h5>
                <SubscriptionDetailTable
                  subscriptionDetail={subscriptionDetail}
                />
              </div>
            )}

            {/* 구독 결제 유형 — 정상가(실 구독 매출) · 50% 할인 · 100원 혜택, 결제 경로 (데이터 없으면 숨김) */}
            <SubscriptionPriceTypeSummary subscriptionDetail={subscriptionDetail} />

            {/* 주간 매출 분해 */}
            <WeeklyRevenueChart
              weekly={activeWeekly}
              selectedMonth={selectedMonth}
              selectedPaymentMethod={selectedPaymentMethod}
            />
          </>
        )}
      </div>
    </Layout>
  );
}

export default RevenueAnalysis;
